import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FaGithub, FaStar, FaCodeBranch, FaExternalLinkAlt, FaGitAlt } from 'react-icons/fa'
import { fetchGitHubRepos } from '../../data/githubData'
import SectionHeading from '../shared/SectionHeading'
import './GitHub.css'

const languageColors = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  Python: '#3572A5',
  Java: '#b07219',
  HTML: '#e34c26',
  CSS: '#563d7c',
  'C++': '#f34b7d',
  C: '#555555',
  'C#': '#178600',
  Ruby: '#701516',
  Go: '#00ADD8',
  Rust: '#dea584',
  Swift: '#ffac45',
  Kotlin: '#A97BFF',
  Dart: '#00B4AB',
  PHP: '#4F5D95',
  Shell: '#89e051',
  Scala: '#c22d40',
  Vue: '#41b883',
  default: '#6e44ff',
}

const RepoCard = ({ repo }) => {
  const langColor = languageColors[repo.language] || languageColors.default

  return (
    <motion.a
      className="github-card"
      href={repo.html_url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      <div className="github-card__header">
        <FaGitAlt className="github-card__icon" />
        <span className="github-card__name">
          {repo.name}
        </span>
      </div>

      <p className="github-card__description">
        {repo.readmeDescription}
      </p>

      <div className="github-card__meta">
        {repo.language && (
          <span className="github-card__language">
            <span className="github-card__lang-dot" style={{ backgroundColor: langColor }} />
            {repo.language}
          </span>
        )}
        <span className="github-card__stat">
          <FaStar className="github-card__stat-icon" />
          {repo.stars}
        </span>
        <span className="github-card__stat">
          <FaCodeBranch className="github-card__stat-icon" />
          {repo.forks}
        </span>
      </div>

      {repo.topics && repo.topics.length > 0 && (
        <div className="github-card__topics">
          {repo.topics.slice(0, 5).map((topic, i) => (
            <span key={i} className="github-card__topic">
              {topic}
            </span>
          ))}
        </div>
      )}

      {repo.homepage && (
        <span
          className="github-card__demo"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            window.open(repo.homepage, '_blank', 'noopener,noreferrer')
          }}
        >
          <FaExternalLinkAlt /> Live Demo
        </span>
      )}
    </motion.a>
  )
}

const GitHub = () => {
  const [repos, setRepos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadRepos = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchGitHubRepos('PasinduPerera10', 12)
      if (data.length === 0) {
        setError('No repositories found or unable to connect to GitHub.')
      } else {
        setRepos(data)
      }
    } catch (err) {
      setError('Failed to load GitHub repositories.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRepos()
  }, [])

  return (
    <section className="github-container" id="github">
      <SectionHeading icon={<FaGithub />}>Personal Projects</SectionHeading>

      {loading && (
        <div className="github-loading">
          <div className="github-loading__spinner" />
          <p>Loading repositories from GitHub...</p>
        </div>
      )}

      {error && !loading && (
        <div className="github-error">
          <p>{error}</p>
          <button className="github-retry-btn" onClick={loadRepos}>
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && (
        <motion.div
          className="github-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, staggerChildren: 0.1 }}
        >
          {repos.map(repo => (
            <RepoCard key={repo.id} repo={repo} />
          ))}
        </motion.div>
      )}
    </section>
  )
}

export default GitHub